import { useState, useEffect } from "react";
import ShimmerLoader from "../../components/ShimmerLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchbookingcalendar } from "../../features/hotel/HotelSlice";
import { selectCurrentUser } from "../../features/auth/authSlice";
import Moment from "moment";
import { format, parseISO } from "date-fns";
import { Funnel } from "lucide-react";
import CurFt from "../../components/CurFt";
import CursorPagination from "../../components/CursorPagination";
import useDocumentTitle from "../../hooks/useDocumentTitle";

export default function HotelBookingCalendar() {
  useDocumentTitle("Booking Calendar");
  const dispatch = useDispatch();
  const { bookingcalendarStatus, bookingcalendar } = useSelector((state) => state.hotel);
  const perPage = 25;
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [BOOKINGSTATUS, SetBOOKINGSTATUS] = useState({});
  const [BookingHistory, SetBookingHistory] = useState([]);
  const user = useSelector(selectCurrentUser);
  const [showFilters, setShowFilters] = useState(false);
  const [FilterData, setFilterData] = useState({
    filterType: "Hotel",
    bookingID: "",
    BookingStatus: [1, 20, 21, 22, 26, 27, 28, 29, 30, 3],
    JourneyType: "All",
    Type: "All",
    FromDate: null,
    ToDate: null,
    TravelFromDate: null,
    TravelToDate: null,
    validate: true,
  });
  const fetchBookingHistory = async (cursor = null) => {
    dispatch(fetchbookingcalendar({ FilterData, perPage, cursor }));
  };
  useEffect(() => {
    fetchBookingHistory();
  }, [FilterData]);

  useEffect(() => {
    const data = bookingcalendar?.Booking;
    SetBookingHistory(data?.data || []);
    SetBOOKINGSTATUS(bookingcalendar?.BOOKINGSTATUS || {});
    setNextCursor(data?.next_cursor);
    setPrevCursor(data?.prev_cursor);
  }, [bookingcalendarStatus, bookingcalendar]);

  const handleNextPage = () => {
    if (nextCursor) {
      fetchBookingHistory(nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (prevCursor) {
      fetchBookingHistory(prevCursor);
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (field, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  return (
    <div className="bg-gray-100 text-gray-800 relative min-h-screen">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 space-y-6">
        <div className="bg-white shadow-md rounded-lg p-4 mt-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Recent Booking</h3>
            <span className="font-semibold text-blue-500 flex items-center gap-1">
              <div className="relative inline-block text-left">
                <button
                  onClick={toggleFilters}
                  className={`px-5 py-2 rounded font-medium shadow transition flex items-center gap-2 ${showFilters ? "bg-blue-600 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                >
                  <Funnel size={18} />
                  Filters
                </button>
              </div>
            </span>
          </div>
        </div>
        {showFilters && (
          <div className="bg-white shadow-md rounded-lg p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* First Row */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking ID</label>
                  <input
                    type="text"
                    value={FilterData.bookingID}
                    onChange={(e) => handleFilterChange("bookingID", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Booking ID"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Journey Type</label>
                  <select
                    value={FilterData.JourneyType}
                    onChange={(e) => handleFilterChange("JourneyType", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="All">All</option>
                    <option value="0">Domestic</option>
                    <option value="1">International</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking From Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={FilterData.FromDate ? new Date(FilterData.FromDate).toISOString().split("T")[0] : ""}
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("FromDate", formattedDate);
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.FromDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("FromDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking To Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={FilterData.ToDate ? new Date(FilterData.ToDate).toISOString().split("T")[0] : ""}
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("ToDate", formattedDate);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.ToDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("ToDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Travel From Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={
                        FilterData.TravelFromDate ? new Date(FilterData.TravelFromDate).toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("TravelFromDate", formattedDate);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.TravelFromDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("TravelFromDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Second Row */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Travel To Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={
                        FilterData.TravelToDate ? new Date(FilterData.TravelToDate).toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("TravelToDate", formattedDate);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.TravelToDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("TravelToDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Empty columns for spacing in second row */}
              <div className="space-y-4"></div>
              <div className="space-y-4"></div>
              <div className="space-y-4"></div>

              {/* Close Button in second row */}
              <div className="space-y-4">
                <div className="pt-6">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Close Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-2">
          <div className="overflow-x-auto">
            <table className="min-w-full border  text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 border text-left">No.</th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Trx Date
                  </th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Travel Date/Time
                  </th>
                  <th className="px-3 py-2 border text-left">Booking ID</th>
                  <th className="px-3 py-2 border text-left">Summary</th>

                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Sales Price
                    {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                      <>
                        <br />
                        My Cost
                      </>
                    )}
                  </th>
                  {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                    <th className="px-3 py-2 border text-left" nowrap="true">
                      Earning
                    </th>
                  )}

                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Passenger Name
                  </th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    {" "}
                    Status
                  </th>
                  <th className="px-3 py-2 border text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookingcalendarStatus === "loading" ? (
                  <>
                    <tr className="border-b">
                      <td className="px-3 py-2 border" colSpan={12}>
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    {BookingHistory.length > 0 ? (
                      BookingHistory.map((item, key) => (
                        <tr className="border-b" key={key}>
                          <td className="px-3 py-2 border">{key + 1}</td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {format(parseISO(item.created_at), "dd MMM, yyyy")}
                            <br />
                            {Moment(item.created_at).format("h:mm A")}
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {Moment(item.checkinDate).format("ddd Do MMM")}
                            <br />
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            <a
                              target="_blank"
                              style={{ color: "blue", textDecoration: "underline" }}
                              href={`/hotel/paymentconfirmation/${item.BookingID}`}
                            >
                              {item.BookingID}
                            </a>
                            <br />
                          </td>
                          <td className="px-3 py-2 border">{item.HotelName + "," + item.city + ", " + item.country}</td>
                          <td className="px-3 py-2 border">
                            <CurFt
                              currency={item?.Currncy || "INR"}
                              price={parseFloat(item.price) + parseFloat(item.agent_markup)}
                            />
                            {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                              <>
                                <br />
                                <CurFt currency={item?.Currncy || "INR"} price={parseFloat(item.price)} />
                              </>
                            )}
                          </td>
                          {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                            <td className="px-3 py-2 border">
                              <CurFt currency={item?.Currncy || "INR"} price={parseFloat(item.agent_markup)} />
                            </td>
                          )}
                          <td className="px-3 py-2 border" nowrap="true">
                            {item.customerName} <br />
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {item.BookingStatus == 1 || item.BookingStatus == 29 ? (
                              <span style={{ color: "green" }}>{BOOKINGSTATUS[item.BookingStatus]}</span>
                            ) : (
                              <span style={{ color: "red" }}>{BOOKINGSTATUS[item.BookingStatus]}</span>
                            )}
                          </td>
                          <td className="text-right px-3 py-2 border">
                            <a
                              target="_blank"
                              style={{ color: "blue", textDecoration: "underline" }}
                              href={`/hotel/paymentconfirmation/${item.BookingID}`}
                            >
                              View
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <tr className="border-b">
                          <td colSpan={12} className="px-3 py-2 border">
                            No record found
                          </td>
                        </tr>
                      </>
                    )}
                  </>
                )}
              </tbody>
            </table>
            <CursorPagination
              nextCursor={nextCursor}
              prevCursor={prevCursor}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
